import _ from 'lodash';

import { AiChatSignalREventType, AiChatSignalRMessageType } from 'constants/aiChat';
import { jsonStringify } from 'utils/json';

import {
  citationsExtractedAction,
  executionCancelledAction,
  messageContentBlockAddedAction,
  messageContentBlockUpdatedAction,
  messageFailedAction,
  messageReceivedAction,
  planCancelledAction,
  planCompletedAction,
  planCreatedAction,
  planPlanningStartedAction,
  planRevisedAction,
  planStepAwaitingConfirmationAction,
  planStepCancelledAction,
  planStepCompletedAction,
  planStepFailedAction,
  planStepSkippedAction,
  planStepStartedAction,
  planStepToolDispatchedAction,
  planStepToolReturnedAction,
  planUpdatedAction,
  responseCompleteAction,
  responseTokenAction,
  sessionStoppedAction,
  stepCompletedAction,
  stepFailedAction,
  stepStartedAction,
  toolCancelledAction,
  toolCompletedAction,
  toolConfirmationRequestedAction,
  toolConfirmationTimedOutAction,
  toolExecutedAction,
  toolFailedAction,
  toolStartedAction,
} from 'store/aiChat/actions';

class SignalRAiChatController {
  #connection;
  #dispatch;
  #logger;
  #sessionId;
  // DEV-3061: remember the companyId of the active join so resubscribe() can
  // re-join after an automatic reconnect (CONNECT_TO_AI_CHAT_HUB needs both).
  #companyId;
  #frontendToolHandler;

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
    this.#frontendToolHandler = null;
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use AI chat SignalR connection without active connection');
    }

    return this.#connection;
  }

  get sessionId() {
    return this.#sessionId;
  }

  connect({ dispatch, frontendToolHandler }) {
    if (!dispatch) {
      this.#logger.error('Dispatch is required for initializing AI chat SignalR controller');

      return;
    }

    if (frontendToolHandler) {
      this.#frontendToolHandler = frontendToolHandler;
    }

    if (this.#dispatch) {
      this.#dispatch = dispatch;

      return;
    }

    this.#dispatch = dispatch;
    this.#startListeners();
  }

  disconnect() {
    if (!this.#dispatch) {
      return;
    }

    this.#stopListeners();
    this.#sessionId = null;
    this.#companyId = null;
    this.#dispatch = null;
  }

  async joinSession(sessionId, companyId) {
    if (!sessionId || !companyId) {
      return;
    }

    if (!this.#connection?.isConnected) {
      this.#logger.warn('[AiChat SignalR] Skipping join — connection is not active', sessionId);

      return;
    }

    try {
      await this.connection.invoke(
        AiChatSignalRMessageType.CONNECT_TO_AI_CHAT_HUB,
        sessionId,
        companyId,
      );
      this.#sessionId = sessionId;
      this.#companyId = companyId;
    } catch (error) {
      this.#logger.error('[AiChat SignalR] Failed to join session', sessionId, error);
    }
  }

  async leaveSession(sessionId) {
    const targetSessionId = sessionId ?? this.#sessionId;

    if (!targetSessionId) {
      return;
    }

    if (!this.#connection?.isConnected) {
      if (targetSessionId === this.#sessionId) {
        this.#sessionId = null;
        this.#companyId = null;
      }

      return;
    }

    try {
      await this.connection.invoke(
        AiChatSignalRMessageType.DISCONNECT_FROM_AI_CHAT_HUB,
        targetSessionId,
      );
    } catch (error) {
      this.#logger.error('[AiChat SignalR] Failed to leave session', targetSessionId, error);
    }

    if (targetSessionId === this.#sessionId) {
      this.#sessionId = null;
      this.#companyId = null;
    }
  }

  // DEV-3061: re-join the tracked session group after an automatic reconnect.
  // withAutomaticReconnect brings the socket back with a NEW connection id, so
  // the server-side JoinSession membership from the initial join is lost and
  // streamed response_token / tool / plan events stop arriving until a manual
  // remount. Mirrors SignalRAiProcessRunsController.resubscribe (DEV-3055).
  async resubscribe() {
    if (!this.#sessionId || !this.#companyId) {
      return;
    }

    await this.joinSession(this.#sessionId, this.#companyId);
  }

  #startListeners() {
    this.connection.on(
      AiChatSignalREventType.PLAN_PLANNING_STARTED,
      this.#onPlanPlanningStarted.bind(this),
    );
    this.connection.on(AiChatSignalREventType.PLAN_CREATED, this.#onPlanCreated.bind(this));
    this.connection.on(AiChatSignalREventType.PLAN_UPDATED, this.#onPlanUpdated.bind(this));
    this.connection.on(AiChatSignalREventType.STEP_STARTED, this.#onStepStarted.bind(this));
    this.connection.on(AiChatSignalREventType.STEP_COMPLETED, this.#onStepCompleted.bind(this));
    this.connection.on(AiChatSignalREventType.STEP_FAILED, this.#onStepFailed.bind(this));
    this.connection.on(AiChatSignalREventType.TOOL_STARTED, this.#onToolStarted.bind(this));
    this.connection.on(AiChatSignalREventType.TOOL_COMPLETED, this.#onToolCompleted.bind(this));
    this.connection.on(AiChatSignalREventType.TOOL_FAILED, this.#onToolFailed.bind(this));
    this.connection.on(
      AiChatSignalREventType.TOOL_CONFIRMATION_REQUESTED,
      this.#onToolConfirmationRequested.bind(this),
    );
    this.connection.on(AiChatSignalREventType.RESPONSE_TOKEN, this.#onResponseToken.bind(this));
    this.connection.on(
      AiChatSignalREventType.RESPONSE_COMPLETE,
      this.#onResponseComplete.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.EXECUTION_CANCELLED,
      this.#onExecutionCancelled.bind(this),
    );
    this.connection.on(AiChatSignalREventType.MESSAGE_FAILED, this.#onMessageFailed.bind(this));
    this.connection.on(
      AiChatSignalREventType.CITATIONS_EXTRACTED,
      this.#onCitationsExtracted.bind(this),
    );
    this.connection.on(AiChatSignalREventType.MESSAGE_RECEIVED, this.#onMessageReceived.bind(this));
    this.connection.on(
      AiChatSignalREventType.MESSAGE_CONTENT_BLOCK_ADDED,
      this.#onMessageContentBlockAdded.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.MESSAGE_CONTENT_BLOCK_UPDATED,
      this.#onMessageContentBlockUpdated.bind(this),
    );
    this.connection.on(AiChatSignalREventType.TOOL_EXECUTED, this.#onToolExecuted.bind(this));
    this.connection.on(AiChatSignalREventType.TOOL_CANCELLED, this.#onToolCancelled.bind(this));
    this.connection.on(
      AiChatSignalREventType.TOOL_CONFIRMATION_TIMEOUT,
      this.#onToolConfirmationTimeout.bind(this),
    );
    this.connection.on(AiChatSignalREventType.PLAN_REVISED, this.#onPlanRevised.bind(this));
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_STARTED,
      this.#onPlanStepStarted.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_TOOL_DISPATCHED,
      this.#onPlanStepToolDispatched.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_TOOL_RETURNED,
      this.#onPlanStepToolReturned.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_COMPLETED,
      this.#onPlanStepCompleted.bind(this),
    );
    this.connection.on(AiChatSignalREventType.PLAN_STEP_FAILED, this.#onPlanStepFailed.bind(this));
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_SKIPPED,
      this.#onPlanStepSkipped.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_AWAITING_CONFIRMATION,
      this.#onPlanStepAwaitingConfirmation.bind(this),
    );
    this.connection.on(
      AiChatSignalREventType.PLAN_STEP_CANCELLED,
      this.#onPlanStepCancelled.bind(this),
    );
    this.connection.on(AiChatSignalREventType.PLAN_COMPLETED, this.#onPlanCompleted.bind(this));
    this.connection.on(AiChatSignalREventType.PLAN_CANCELLED, this.#onPlanCancelled.bind(this));
    this.connection.on(
      AiChatSignalREventType.TOOL_EXECUTION_REQUESTED,
      this.#onToolExecutionRequested.bind(this),
    );
    this.connection.on(AiChatSignalREventType.ROUTER_CLASSIFIED, _.noop);
    this.connection.on(AiChatSignalREventType.SESSION_STOPPED, this.#onSessionStopped.bind(this));
  }

  #stopListeners() {
    this.connection.off(AiChatSignalREventType.PLAN_CREATED);
    this.connection.off(AiChatSignalREventType.PLAN_UPDATED);
    this.connection.off(AiChatSignalREventType.STEP_STARTED);
    this.connection.off(AiChatSignalREventType.STEP_COMPLETED);
    this.connection.off(AiChatSignalREventType.STEP_FAILED);
    this.connection.off(AiChatSignalREventType.TOOL_STARTED);
    this.connection.off(AiChatSignalREventType.TOOL_COMPLETED);
    this.connection.off(AiChatSignalREventType.TOOL_FAILED);
    this.connection.off(AiChatSignalREventType.TOOL_CONFIRMATION_REQUESTED);
    this.connection.off(AiChatSignalREventType.RESPONSE_TOKEN);
    this.connection.off(AiChatSignalREventType.RESPONSE_COMPLETE);
    this.connection.off(AiChatSignalREventType.EXECUTION_CANCELLED);
    this.connection.off(AiChatSignalREventType.MESSAGE_FAILED);
    this.connection.off(AiChatSignalREventType.CITATIONS_EXTRACTED);
    this.connection.off(AiChatSignalREventType.MESSAGE_RECEIVED);
    this.connection.off(AiChatSignalREventType.MESSAGE_CONTENT_BLOCK_ADDED);
    this.connection.off(AiChatSignalREventType.MESSAGE_CONTENT_BLOCK_UPDATED);
    this.connection.off(AiChatSignalREventType.TOOL_EXECUTED);
    this.connection.off(AiChatSignalREventType.TOOL_CANCELLED);
    this.connection.off(AiChatSignalREventType.TOOL_CONFIRMATION_TIMEOUT);
    this.connection.off(AiChatSignalREventType.PLAN_REVISED);
    this.connection.off(AiChatSignalREventType.PLAN_STEP_STARTED);
    this.connection.off(AiChatSignalREventType.PLAN_STEP_TOOL_DISPATCHED);
    this.connection.off(AiChatSignalREventType.PLAN_STEP_TOOL_RETURNED);
    this.connection.off(AiChatSignalREventType.PLAN_STEP_COMPLETED);
    this.connection.off(AiChatSignalREventType.PLAN_STEP_FAILED);
    this.connection.off(AiChatSignalREventType.PLAN_COMPLETED);
    this.connection.off(AiChatSignalREventType.PLAN_CANCELLED);
    this.connection.off(AiChatSignalREventType.TOOL_EXECUTION_REQUESTED);
    this.connection.off(AiChatSignalREventType.ROUTER_CLASSIFIED);
    this.connection.off(AiChatSignalREventType.SESSION_STOPPED);
  }

  async respondToToolConfirmation({
    sessionId,
    companyId,
    toolCallId,
    approved,
    reason,
    selectedSlaGroupId,
    conditionDecisions,
  }) {
    const targetSessionId = sessionId ?? this.#sessionId;
    const targetCompanyId = companyId ?? this.#companyId;

    if (!targetSessionId || !targetCompanyId || !toolCallId) {
      this.#logger.error(
        '[AiChat SignalR] respondToToolConfirmation requires sessionId, companyId and toolCallId',
      );

      throw new Error('respondToToolConfirmation requires sessionId, companyId and toolCallId');
    }

    // Backend AiChatController.HandleRespondToToolConfirmation arg layout (DEV-3192 + DEV-4455):
    // [sessionId, companyId, toolCallId, outcome, resolutionReason, resultPayload].
    // It persists arg 5 (resultPayload) onto the chat pending row; ChatMessageProcessor
    // parses it back via ConfirmationResolutionPayload to recover BOTH the operator's
    // PromptUser SLA-group choice AND the per-condition Keep/Edit/Remove/Reject decisions.
    // So fold both into that single arg: a bare SLA-group GUID when there are no
    // per-condition decisions (DEV-3192 back-compat), else the resolution JSON envelope.
    const resultPayload = _.isEmpty(conditionDecisions)
      ? (selectedSlaGroupId ?? null)
      : jsonStringify({ selectedSlaGroupId: selectedSlaGroupId ?? null, conditionDecisions });

    await this.connection.invoke(
      AiChatSignalRMessageType.RESPOND_TO_TOOL_CONFIRMATION,
      targetSessionId,
      targetCompanyId,
      toolCallId,
      approved,
      reason ?? null,
      resultPayload,
    );
  }

  #onPlanPlanningStarted(payload) {
    this.#dispatch(planPlanningStartedAction(payload));
  }

  #onPlanCreated(payload) {
    this.#dispatch(planCreatedAction(payload));
  }

  #onPlanUpdated(payload) {
    this.#dispatch(planUpdatedAction(payload));
  }

  #onStepStarted(payload) {
    this.#dispatch(stepStartedAction(payload));
  }

  #onStepCompleted(payload) {
    this.#dispatch(stepCompletedAction(payload));
  }

  #onStepFailed(payload) {
    this.#dispatch(stepFailedAction(payload));
  }

  #onToolStarted(payload) {
    this.#dispatch(toolStartedAction(payload));
  }

  #onToolCompleted(payload) {
    this.#dispatch(toolCompletedAction(payload));
  }

  #onToolFailed(payload) {
    this.#dispatch(toolFailedAction(payload));
  }

  #onToolConfirmationRequested(payload) {
    this.#dispatch(toolConfirmationRequestedAction(payload));
  }

  #onResponseToken(payload) {
    this.#dispatch(responseTokenAction(payload));
  }

  #onResponseComplete(payload) {
    this.#dispatch(responseCompleteAction(payload));
  }

  #onExecutionCancelled(payload) {
    this.#dispatch(executionCancelledAction(payload));
  }

  #onSessionStopped(payload) {
    this.#dispatch(sessionStoppedAction(payload));
  }

  #onMessageFailed(payload) {
    this.#dispatch(messageFailedAction(payload));
  }

  #onCitationsExtracted(payload) {
    this.#dispatch(citationsExtractedAction(payload));
  }

  #onMessageReceived(payload) {
    this.#dispatch(messageReceivedAction(payload));
  }

  #onMessageContentBlockAdded(payload) {
    this.#dispatch(messageContentBlockAddedAction(payload));
  }

  #onMessageContentBlockUpdated(payload) {
    this.#dispatch(messageContentBlockUpdatedAction(payload));
  }

  #onToolExecuted(payload) {
    this.#dispatch(toolExecutedAction(payload));
  }

  #onToolCancelled(payload) {
    this.#dispatch(toolCancelledAction(payload));
  }

  #onToolConfirmationTimeout(payload) {
    this.#dispatch(toolConfirmationTimedOutAction(payload));
  }

  #onPlanRevised(payload) {
    this.#dispatch(planRevisedAction(payload));
  }

  #onPlanStepStarted(payload) {
    this.#dispatch(planStepStartedAction(payload));
  }

  #onPlanStepToolDispatched(payload) {
    this.#dispatch(planStepToolDispatchedAction(payload));
  }

  #onPlanStepToolReturned(payload) {
    this.#dispatch(planStepToolReturnedAction(payload));
  }

  #onPlanStepCompleted(payload) {
    this.#dispatch(planStepCompletedAction(payload));
  }

  #onPlanStepFailed(payload) {
    this.#dispatch(planStepFailedAction(payload));
  }

  #onPlanStepSkipped(payload) {
    this.#dispatch(planStepSkippedAction(payload));
  }

  #onPlanStepAwaitingConfirmation(payload) {
    this.#dispatch(planStepAwaitingConfirmationAction(payload));
  }

  #onPlanStepCancelled(payload) {
    this.#dispatch(planStepCancelledAction(payload));
  }

  #onPlanCompleted(payload) {
    this.#dispatch(planCompletedAction(payload));
  }

  #onPlanCancelled(payload) {
    this.#dispatch(planCancelledAction(payload));
  }

  #onToolExecutionRequested(payload) {
    if (!this.#frontendToolHandler) {
      this.#logger.warn(
        '[AiChat SignalR] tool_execution_requested received but no FrontendToolHandler attached',
      );

      return;
    }

    this.#frontendToolHandler.handleToolExecutionRequest(payload);
  }
}

export default SignalRAiChatController;
